import { useState, useMemo } from 'react';
import { currentCohortWeek } from '@/lib/cohortWeeks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, BookOpen, FileCheck, Brain, Lightbulb, AlertTriangle, Download, ExternalLink, Check, X, Search, Trash2, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { CohortParticipantRow, ScheduleItemRow } from '@/hooks/useCohortManagement';

interface CohortProgressTabProps {
  cohortId: string;
  participants: CohortParticipantRow[];
  participantsLoading: boolean;
  scheduleItems: Partial<ScheduleItemRow>[];
  cohortStartDate?: string | null;
  cohortName?: string | null;
}

interface ContentItem {
  id: string;
  title: string;
  completed: boolean;
}

interface DeliverableItem {
  id: string | null; // UUID del participant_deliverables (null si missing)
  workshop_id: string | null;
  workshop_title: string;
  name: string;
  status: 'missing' | 'not_started' | 'submitted' | 'validated' | string;
  file_url: string | null;
  week_number: number | null;
}

interface ContentItemFull extends ContentItem {
  week_number: number | null;
}

interface ProjectItem {
  id: string;
  name: string;
  submitted: boolean;
  status: string | null;
}

interface ParticipantRaw {
  user_id: string;
  full_name: string;
  email: string;
  allContentItems: ContentItemFull[];
  allDeliverableItems: DeliverableItem[];
  diagnosticCompleted: boolean;
  projectItems: ProjectItem[];
  challengeLabSolutions: number;
  challengeLabChallenges: number;
}

interface ParticipantProgress {
  user_id: string;
  full_name: string;
  email: string;
  contentPercent: number;
  contentItems: ContentItem[];
  deliverablesSubmitted: number;
  deliverablesValidated: number;
  deliverablesTotal: number;
  deliverableItems: DeliverableItem[];
  diagnosticCompleted: boolean;
  projectsCount: number;
  projectsSubmitted: number;
  projectItems: ProjectItem[];
  challengeLabSolutions: number;
  challengeLabChallenges: number;
}

interface RiskAlert {
  user_id: string;
  full_name: string;
  reasons: string[];
  severity: 'high' | 'medium';
}

interface RiskContext {
  currentWeek: number;
  projectDeadline: string | null;
  projectDeadlineWeek: number | null;
}

// Alertas basadas en plazos reales: solo se exige lo que ya debió hacerse
// según la semana actual de la cohorte y la fecha límite del proyecto.
function computeRiskAlerts(raw: ParticipantRaw[], ctx: RiskContext): RiskAlert[] {
  const { currentWeek, projectDeadline, projectDeadlineWeek } = ctx;
  if (currentWeek <= 0) return [];
  const alerts: RiskAlert[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let daysToDeadline: number | null = null;
  if (projectDeadline) {
    const dl = new Date(projectDeadline.slice(0, 10) + 'T00:00:00');
    daysToDeadline = Math.round((dl.getTime() - today.getTime()) / 86400000);
  }

  for (const p of raw) {
    const reasons: string[] = [];
    let high = false;

    // Contenido obligatorio de semanas ya iniciadas sin completar
    const overdueContent = p.allContentItems.filter(
      c => !c.completed && c.week_number !== null && c.week_number <= currentWeek
    ).length;
    if (overdueContent >= 3) reasons.push(`${overdueContent} contenidos atrasados`);

    // Entregables de semanas pasadas sin enviar (la semana en curso aún no vence)
    const overdueDeliverables = p.allDeliverableItems.filter(
      d => d.workshop_id !== null && d.week_number !== null && d.week_number < currentWeek &&
        (d.status === 'missing' || d.status === 'not_started')
    ).length;
    if (overdueDeliverables > 0) {
      reasons.push(`${overdueDeliverables} entregable${overdueDeliverables > 1 ? 's' : ''} vencido${overdueDeliverables > 1 ? 's' : ''}`);
      high = true;
    }

    // Diagnóstico: solo se exige desde la semana 3
    if (currentWeek >= 3 && !p.diagnosticCompleted) reasons.push('Diagnóstico sin hacer');

    // Proyecto: solo alerta cerca de la fecha límite o una vez vencida
    const projectSubmitted = p.projectItems.some(pr => pr.submitted);
    if (!projectSubmitted) {
      if (daysToDeadline !== null) {
        if (daysToDeadline < 0) {
          reasons.push('Proyecto vencido sin entregar');
          high = true;
        } else if (daysToDeadline <= 7) {
          reasons.push(`Proyecto sin entregar (vence en ${daysToDeadline} día${daysToDeadline === 1 ? '' : 's'})`);
        }
      } else if (projectDeadlineWeek !== null && currentWeek >= projectDeadlineWeek) {
        reasons.push('Proyecto sin entregar');
        high = true;
      }
    }

    // Sin ninguna actividad en semanas ya iniciadas
    const anyActivity =
      p.allContentItems.some(c => c.completed) ||
      p.allDeliverableItems.some(d => d.status === 'submitted' || d.status === 'validated');
    if (currentWeek >= 2 && !anyActivity) {
      reasons.push('Sin actividad en la plataforma');
      high = true;
    }

    if (reasons.length > 0) {
      alerts.push({
        user_id: p.user_id,
        full_name: p.full_name,
        reasons,
        severity: high || reasons.length >= 3 ? 'high' : 'medium',
      });
    }
  }
  return alerts.sort((a, b) => b.reasons.length - a.reasons.length);
}

export function CohortProgressTab({ cohortId, participants, participantsLoading, scheduleItems, cohortStartDate, cohortName }: CohortProgressTabProps) {
  const userIds = participants.map(p => p.user_id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const [contentModal, setContentModal] = useState<ParticipantProgress | null>(null);
  const [deliverableModal, setDeliverableModal] = useState<ParticipantProgress | null>(null);
  const [projectsModal, setProjectsModal] = useState<ParticipantProgress | null>(null);
  const [deliverableToDelete, setDeliverableToDelete] = useState<DeliverableItem | null>(null);

  const deleteDeliverable = useMutation({
    mutationFn: async (deliverableId: string) => {
      const { error } = await supabase
        .from('participant_deliverables')
        .delete()
        .eq('id', deliverableId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Entregable eliminado', description: 'El entregable se eliminó correctamente.' });
      queryClient.invalidateQueries({ queryKey: ['cohort-progress', cohortId] });
      // Optimistic: limpia el modal abierto si el item eliminado es de él
      setDeliverableModal(prev => prev ? {
        ...prev,
        deliverableItems: prev.deliverableItems.map(it =>
          it.id === deliverableToDelete?.id
            ? { ...it, id: null, status: 'missing', file_url: null, name: '—' }
            : it
        ),
        deliverablesSubmitted: Math.max(0, prev.deliverablesSubmitted - (deliverableToDelete?.status === 'submitted' || deliverableToDelete?.status === 'validated' ? 1 : 0)),
        deliverablesValidated: Math.max(0, prev.deliverablesValidated - (deliverableToDelete?.status === 'validated' ? 1 : 0)),
      } : null);
      setDeliverableToDelete(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error al eliminar', description: err.message, variant: 'destructive' });
    },
  });
  const [scopeMode, setScopeMode] = useState<'total' | 'current_week'>('total');
  const [searchParticipant, setSearchParticipant] = useState('');

  const currentWeek = useMemo(() => currentCohortWeek(cohortStartDate), [cohortStartDate]);

  const progressQuery = useQuery({
    queryKey: ['cohort-progress', cohortId, userIds.length],
    queryFn: async (): Promise<ParticipantRaw[]> => {
      if (userIds.length === 0) return [];

      const { data: cohortRow } = await supabase.from('cohorts').select('program_id').eq('id', cohortId).single();
      let workshops: { id: string; title: string; requires_deliverable: boolean | null; week_number: number | null }[] = [];
      if (cohortRow?.program_id) {
        const { data: ws } = await supabase
          .from('program_workshops')
          .select('id, title, requires_deliverable, week_number')
          .eq('program_id', cohortRow.program_id)
          .is('deleted_at', null)
          .order('sort_order', { ascending: true, nullsFirst: false });
        workshops = (ws as any) || [];
      }
      const requiredWorkshops = workshops.filter(w => w.requires_deliverable);

      // Snapshot de currículo de la cohorte: si existe, solo cuentan esos content_id
      // (fallback al programa vivo si no hay snapshot).
      const { data: snapRows } = await supabase
        .from('cohort_required_content').select('content_id').eq('cohort_id', cohortId);
      const snapshotSet = new Set((snapRows ?? []).map((r: any) => r.content_id));
      const hasSnapshot = snapshotSet.size > 0;

      const mandatoryContent: { id: string; name: string; week_number: number | null }[] = [];
      if (cohortRow?.program_id) {
        const { data: chapters } = await supabase
          .from('program_chapters')
          .select('id, week_number')
          .eq('program_id', cohortRow.program_id);
        const chaptersList = (chapters as any) || [];
        const chapterIds = chaptersList.map((c: any) => c.id);
        const chapterWeekById: Record<string, number | null> = {};
        chaptersList.forEach((c: any) => { chapterWeekById[c.id] = c.week_number ?? null; });
        if (chapterIds.length > 0) {
          const { data: cc } = await supabase
            .from('chapter_contents')
            .select('content_id, chapter_id, is_mandatory, content_bank!inner(id, name)')
            .in('chapter_id', chapterIds)
            .eq('is_mandatory', true);
          const seenWeek: Record<string, number | null> = {};
          (cc || []).forEach((row: any) => {
            const cb = row.content_bank;
            if (!cb) return;
            if (hasSnapshot && !snapshotSet.has(cb.id)) return; // solo lo congelado en el snapshot
            const w = chapterWeekById[row.chapter_id] ?? null;
            if (!(cb.id in seenWeek)) {
              seenWeek[cb.id] = w;
              mandatoryContent.push({ id: cb.id, name: cb.name, week_number: w });
            } else {
              const prev = seenWeek[cb.id];
              if (w !== null && (prev === null || w < prev)) {
                seenWeek[cb.id] = w;
                const idx = mandatoryContent.findIndex(m => m.id === cb.id);
                if (idx >= 0) mandatoryContent[idx].week_number = w;
              }
            }
          });
        }
      }

      // Helper to paginate around Supabase's default 1000-row cap.
      // CRÍTICO: agrega `.order('id')` para que LIMIT/OFFSET sea determinístico.
      // Sin un ORDER BY estable, Postgres devuelve filas en orden indefinido y
      // entre páginas se pierden o duplican filas — por ej. cohortes con > 1000
      // content_progress mostraban usuarios con progreso incompleto aunque la BD
      // los tuviera todos como completed=true.
      const fetchAllPaged = async <T,>(builder: () => any): Promise<T[]> => {
        const pageSize = 1000;
        let from = 0;
        const all: T[] = [];
        // Cap at 50k rows as a safety net
        for (let i = 0; i < 50; i++) {
          const { data, error } = await builder().order('id').range(from, from + pageSize - 1);
          if (error) throw error;
          const rows = (data as T[]) || [];
          all.push(...rows);
          if (rows.length < pageSize) break;
          from += pageSize;
        }
        return all;
      };

      // 1) Traer collaborator links para estos participantes: nos dice en qué
      //    proyectos externos aparecen como co-participantes. Luego traeremos
      //    esos proyectos aunque su cohort_id sea el del dueño (u otro).
      const collabLinksRaw = await fetchAllPaged<any>(() =>
        supabase.from('project_collaborators').select('project_id, user_id').in('user_id', userIds)
      );
      const collabProjectIds = Array.from(new Set(collabLinksRaw.map((r: any) => r.project_id).filter(Boolean)));

      const [contentRes, deliverablesRes, diagnosticsRes, ownedProjectsRes, collabProjectsRes, challengesRes, solutionsRes] = await Promise.all([
        fetchAllPaged<any>(() => supabase.from('content_progress').select('user_id, content_id, completed').in('user_id', userIds)).then(data => ({ data })),
        fetchAllPaged<any>(() => supabase.from('participant_deliverables').select('id, user_id, status, name, file_url, workshop_id').in('user_id', userIds).eq('cohort_id', cohortId)).then(data => ({ data })),
        fetchAllPaged<any>(() => supabase.from('participant_diagnostics').select('user_id, completed_at').in('user_id', userIds)).then(data => ({ data })),
        fetchAllPaged<any>(() => supabase.from('participant_projects').select('id, user_id, name, submitted, status').in('user_id', userIds).eq('cohort_id', cohortId)).then(data => ({ data })),
        collabProjectIds.length > 0
          ? fetchAllPaged<any>(() => supabase.from('participant_projects').select('id, user_id, name, submitted, status').in('id', collabProjectIds)).then(data => ({ data }))
          : Promise.resolve({ data: [] as any[] }),
        fetchAllPaged<any>(() => supabase.from('challenges').select('id, user_id').in('user_id', userIds)).then(data => ({ data })),
        fetchAllPaged<any>(() => supabase.from('challenge_solutions').select('id, challenge_id, challenges!inner(user_id)').in('challenges.user_id', userIds)).then(data => ({ data })),
      ]);


      const completedContentByUser: Record<string, Set<string>> = {};
      (contentRes.data || []).forEach((r: any) => {
        if (!r.completed) return;
        if (!completedContentByUser[r.user_id]) completedContentByUser[r.user_id] = new Set();
        completedContentByUser[r.user_id].add(r.content_id);
      });

      const deliverablesByUser: Record<string, Record<string, { id: string; status: string; name: string; file_url: string | null }>> = {};
      const deliverablesNoWorkshopByUser: Record<string, { id: string; status: string; name: string; file_url: string | null }[]> = {};
      (deliverablesRes.data || []).forEach((r: any) => {
        if (!deliverablesByUser[r.user_id]) deliverablesByUser[r.user_id] = {};
        if (r.workshop_id) {
          deliverablesByUser[r.user_id][r.workshop_id] = {
            id: r.id,
            status: r.status || 'submitted',
            name: r.name || 'Entregable',
            file_url: r.file_url,
          };
        } else {
          if (!deliverablesNoWorkshopByUser[r.user_id]) deliverablesNoWorkshopByUser[r.user_id] = [];
          deliverablesNoWorkshopByUser[r.user_id].push({
            id: r.id,
            status: r.status || 'submitted',
            name: r.name || 'Entregable',
            file_url: r.file_url,
          });
        }
      });

      const diagnosticsByUser = new Set<string>();
      (diagnosticsRes.data || []).forEach((r: any) => {
        if (r.completed_at) diagnosticsByUser.add(r.user_id);
      });

      // Indexa colaboradores por project_id → [user_id, ...]
      const collabsByProject: Record<string, string[]> = {};
      collabLinksRaw.forEach((r: any) => {
        if (!collabsByProject[r.project_id]) collabsByProject[r.project_id] = [];
        collabsByProject[r.project_id].push(r.user_id);
      });

      // Merge proyectos propios + proyectos donde el usuario es colaborador.
      // Un proyecto es UNA fila con status/submitted únicos; se atribuye igual
      // al dueño y a TODOS sus colaboradores (mismos badges Enviado/Borrador).
      const allProjectRows: any[] = [
        ...((ownedProjectsRes.data as any[]) || []),
        ...((collabProjectsRes.data as any[]) || []),
      ];
      const seenProject = new Set<string>();
      const userIdsSet = new Set(userIds);
      const projectsByUser: Record<string, ProjectItem[]> = {};
      allProjectRows.forEach((r: any) => {
        if (seenProject.has(r.id)) return;
        seenProject.add(r.id);
        const item: ProjectItem = {
          id: r.id,
          name: r.name || 'Proyecto sin título',
          submitted: !!r.submitted,
          status: r.status,
        };
        const recipients = new Set<string>();
        if (userIdsSet.has(r.user_id)) recipients.add(r.user_id); // dueño
        (collabsByProject[r.id] || []).forEach((uid) => { if (userIdsSet.has(uid)) recipients.add(uid); });
        recipients.forEach((uid) => {
          if (!projectsByUser[uid]) projectsByUser[uid] = [];
          projectsByUser[uid].push(item);
        });
      });

      const challengesByUser: Record<string, number> = {};
      (challengesRes.data || []).forEach((r: any) => {
        challengesByUser[r.user_id] = (challengesByUser[r.user_id] || 0) + 1;
      });

      const solutionsByUser: Record<string, number> = {};
      (solutionsRes.data || []).forEach((r: any) => {
        const uid = (r.challenges as any)?.user_id;
        if (uid) solutionsByUser[uid] = (solutionsByUser[uid] || 0) + 1;
      });

      return participants.map(p => {
        const completedSet = completedContentByUser[p.user_id] || new Set();
        const allContentItems: ContentItemFull[] = mandatoryContent.map(mc => ({
          id: mc.id,
          title: mc.name,
          completed: completedSet.has(mc.id),
          week_number: mc.week_number,
        }));

        const userDelivs = deliverablesByUser[p.user_id] || {};
        const allDeliverableItems: DeliverableItem[] = requiredWorkshops.map(w => {
          const d = userDelivs[w.id];
          return {
            id: d?.id || null,
            workshop_id: w.id,
            workshop_title: w.title,
            name: d?.name || '—',
            status: d?.status || 'missing',
            file_url: d?.file_url || null,
            week_number: w.week_number,
          };
        });
        Object.entries(userDelivs).forEach(([wid, d]) => {
          if (!requiredWorkshops.find(w => w.id === wid)) {
            const w = workshops.find(x => x.id === wid);
            allDeliverableItems.push({
              id: d.id,
              workshop_id: wid,
              workshop_title: w?.title || 'Sesión en Vivo',
              name: d.name,
              status: d.status,
              file_url: d.file_url,
              week_number: w?.week_number ?? null,
            });
          }
        });
        (deliverablesNoWorkshopByUser[p.user_id] || []).forEach(d => {
          allDeliverableItems.push({
            id: d.id,
            workshop_id: null,
            workshop_title: 'Sin sesión en vivo',
            name: d.name,
            status: d.status,
            file_url: d.file_url,
            week_number: null,
          });
        });

        return {
          user_id: p.user_id,
          full_name: p.full_name,
          email: p.email,
          allContentItems,
          allDeliverableItems,
          diagnosticCompleted: diagnosticsByUser.has(p.user_id),
          projectItems: projectsByUser[p.user_id] || [],
          challengeLabChallenges: challengesByUser[p.user_id] || 0,
          challengeLabSolutions: solutionsByUser[p.user_id] || 0,
        };
      });
    },
    enabled: userIds.length > 0,
  });

  const data: ParticipantProgress[] = useMemo(() => {
    const raw = progressQuery.data || [];
    const filterByWeek = scopeMode === 'current_week';
    const inScope = (wn: number | null) => !filterByWeek || wn === null || (currentWeek > 0 && wn <= currentWeek);
    return raw.map(p => {
      const contentItems: ContentItem[] = p.allContentItems
        .filter(c => inScope(c.week_number))
        .map(({ id, title, completed }) => ({ id, title, completed }));
      const completedCount = contentItems.filter(c => c.completed).length;
      const contentPercent = contentItems.length > 0 ? Math.round((completedCount / contentItems.length) * 100) : 0;

      const deliverableItems = p.allDeliverableItems.filter(d => inScope(d.week_number));
      const deliverablesTotal = deliverableItems.filter(d => d.workshop_id !== null).length;
      const deliverablesSubmitted = deliverableItems.filter(d => d.status === 'submitted' || d.status === 'validated').length;
      const deliverablesValidated = deliverableItems.filter(d => d.status === 'validated').length;

      return {
        user_id: p.user_id,
        full_name: p.full_name,
        email: p.email,
        contentPercent,
        contentItems,
        deliverablesSubmitted,
        deliverablesValidated,
        deliverablesTotal,
        deliverableItems,
        diagnosticCompleted: p.diagnosticCompleted,
        projectsCount: p.projectItems.length,
        projectsSubmitted: p.projectItems.filter(pr => pr.submitted).length,
        projectItems: p.projectItems,
        challengeLabChallenges: p.challengeLabChallenges,
        challengeLabSolutions: p.challengeLabSolutions,
      };
    });
  }, [progressQuery.data, scopeMode, currentWeek]);

  const deadlinesQuery = useQuery({
    queryKey: ['cohort-deadlines', cohortId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cohorts')
        .select('project_deadline, project_deadline_week')
        .eq('id', cohortId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Las alertas se calculan sobre el universo completo (sin filtro de semana)
  // y según los plazos reales de la cohorte.
  const riskAlerts = useMemo(
    () =>
      computeRiskAlerts(progressQuery.data || [], {
        currentWeek,
        projectDeadline: deadlinesQuery.data?.project_deadline ?? null,
        projectDeadlineWeek: deadlinesQuery.data?.project_deadline_week ?? null,
      }),
    [progressQuery.data, currentWeek, deadlinesQuery.data]
  );

  const filteredData = useMemo(() => {
    const q = searchParticipant.trim().toLowerCase();
    if (!q) return data;
    return data.filter(p =>
      p.full_name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q)
    );
  }, [data, searchParticipant]);

  const universeInfo = useMemo(() => {
    const raw = progressQuery.data || [];
    if (raw.length === 0) return { contents: 0, deliverables: 0 };
    const sample = raw[0];
    const filterByWeek = scopeMode === 'current_week';
    const inScope = (wn: number | null) => !filterByWeek || wn === null || (currentWeek > 0 && wn <= currentWeek);
    return {
      contents: sample.allContentItems.filter(c => inScope(c.week_number)).length,
      deliverables: sample.allDeliverableItems.filter(d => d.workshop_id !== null && inScope(d.week_number)).length,
    };
  }, [progressQuery.data, scopeMode, currentWeek]);

  const totalParticipants = data.length;
  const avgContent = totalParticipants > 0 ? Math.round(data.reduce((s, d) => s + d.contentPercent, 0) / totalParticipants) : 0;
  const totalDeliverables = data.reduce((s, d) => s + d.deliverablesSubmitted, 0);
  const totalValidated = data.reduce((s, d) => s + d.deliverablesValidated, 0);
  const diagnosticsCompleted = data.filter(d => d.diagnosticCompleted).length;
  const totalProjects = data.reduce((s, d) => s + d.projectsCount, 0);

  const handleExport = () => {
    if (data.length === 0) return;
    const rows = data.map(r => {
      const done = r.deliverableItems
        .filter(d => d.status === 'submitted' || d.status === 'validated')
        .map(d => d.workshop_title)
        .join(', ');
      return {
        'Participante': r.full_name,
        'Correo': r.email,
        '% Contenido': r.contentPercent,
        'Entregables (enviados/total)': `${r.deliverablesSubmitted}/${r.deliverablesTotal}`,
        'Entregables realizados': done,
        'Lab. Retos': r.challengeLabSolutions > 0 ? 'Sí' : 'No',
        'Diagnóstico': r.diagnosticCompleted ? 'Sí' : 'No',
        'Proyectos creados': r.projectsCount,
        'Proyectos enviados': r.projectsSubmitted,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(k => ({ wch: Math.max(k.length, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Progreso');
    const scope = scopeMode === 'total' ? 'total' : `semana-${currentWeek}`;
    const safeName = (cohortName || 'cohorte').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `progreso_${safeName}_${scope}_${date}.xlsx`);
  };

  if (participantsLoading) {
    return <Card><CardContent className="p-8"><div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div></CardContent></Card>;
  }

  if (participants.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          No hay participantes asignados a esta cohorte. Asigna participantes para ver su progreso.
        </CardContent>
      </Card>
    );
  }

  const renderDeliverableStatusBadge = (status: string) => {
    if (status === 'validated') return <Badge variant="success" className="text-xs">Validado</Badge>;
    if (status === 'submitted') return <Badge variant="outline" className="text-xs">Enviado</Badge>;
    if (status === 'missing' || status === 'not_started') return <Badge variant="destructive" className="text-xs">Faltante</Badge>;
    return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 text-center">
            <BookOpen className="w-5 h-5 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold">{avgContent}%</p>
            <p className="text-xs text-muted-foreground">Contenido promedio</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 text-center">
            <FileCheck className="w-5 h-5 mx-auto mb-1 text-success" />
            <p className="text-2xl font-bold">{totalDeliverables}</p>
            <p className="text-xs text-muted-foreground">Entregables enviados</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 text-center">
            <FileCheck className="w-5 h-5 mx-auto mb-1 text-info" />
            <p className="text-2xl font-bold">{totalValidated}</p>
            <p className="text-xs text-muted-foreground">Entregables validados</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 text-center">
            <Brain className="w-5 h-5 mx-auto mb-1 text-secondary" />
            <p className="text-2xl font-bold">{diagnosticsCompleted}/{totalParticipants}</p>
            <p className="text-xs text-muted-foreground">Diagnósticos completos</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4 text-center">
            <Lightbulb className="w-5 h-5 mx-auto mb-1 text-accent" />
            <p className="text-2xl font-bold">{totalProjects}</p>
            <p className="text-xs text-muted-foreground">Proyectos creados</p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Alerts */}
      {riskAlerts.length > 0 && (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Alertas de Riesgo ({riskAlerts.length})
            </CardTitle>
            <CardDescription>Participantes con entregas vencidas o compromisos próximos a vencer</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {riskAlerts.map(alert => (
                <div key={alert.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-destructive/20 bg-destructive/5">
                  <AlertTriangle className={`w-4 h-4 shrink-0 ${alert.severity === 'high' ? 'text-destructive' : 'text-warning'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{alert.full_name}</p>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {alert.reasons.map((reason, i) => (
                      <Badge
                        key={i}
                        variant={alert.severity === 'high' ? 'destructive' : 'warning'}
                        className="text-[10px]"
                      >
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-participant table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />Progreso por Participante</CardTitle>
              <CardDescription>Haz clic en cualquier celda de Contenido, Entregables o Proyectos para ver el detalle</CardDescription>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <div className="flex items-center gap-2">
                <Tabs value={scopeMode} onValueChange={(v) => setScopeMode(v as 'total' | 'current_week')}>
                  <TabsList>
                    <TabsTrigger value="total">Total de la ruta</TabsTrigger>
                    <TabsTrigger value="current_week" disabled={currentWeek === 0}>
                      {currentWeek === 0 ? 'Ruta no iniciado' : `Hasta semana ${currentWeek}`}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={data.length === 0}
                >
                  <Download className="w-4 h-4" />
                  Descargar reporte (Excel)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {scopeMode === 'total'
                  ? `Mostrando todo la ruta (${universeInfo.contents} contenidos, ${universeInfo.deliverables} entregables)`
                  : `Mostrando hasta semana ${currentWeek} (${universeInfo.contents} contenidos, ${universeInfo.deliverables} entregables)`}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o correo..."
              value={searchParticipant}
              onChange={e => setSearchParticipant(e.target.value)}
              className="pl-10"
            />
          </div>
          {progressQuery.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filteredData.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              {searchParticipant ? 'No hay participantes que coincidan con la búsqueda.' : 'Sin datos de progreso.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participante</TableHead>
                  <TableHead>Contenido</TableHead>
                  <TableHead>Entregables</TableHead>
                  <TableHead>Lab. Retos</TableHead>
                  <TableHead>Diagnóstico</TableHead>
                  <TableHead>Proyectos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map(row => {
                  const isAtRisk = riskAlerts.some(a => a.user_id === row.user_id);
                  return (
                    <TableRow key={row.user_id} className={isAtRisk ? 'bg-destructive/5' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isAtRisk && <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />}
                          <div>
                            <p className="font-medium text-sm">{row.full_name}</p>
                            <p className="text-xs text-muted-foreground">{row.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => setContentModal(row)}
                          className="flex items-center gap-2 min-w-[120px] w-full hover:opacity-80 transition-opacity cursor-pointer group"
                          title="Ver detalle de contenido"
                        >
                          <Progress value={row.contentPercent} className="h-2 flex-1" />
                          <span className="text-xs font-medium w-10 text-right group-hover:underline">{row.contentPercent}%</span>
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => setDeliverableModal(row)}
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                          title="Ver detalle de entregables"
                        >
                          <Badge variant="outline" className="text-xs font-medium hover:bg-muted">
                            {row.deliverablesSubmitted}/{row.deliverablesTotal || '?'}
                          </Badge>
                          {row.deliverablesValidated > 0 && (
                            <Badge variant="success" className="text-xs">{row.deliverablesValidated} ✓</Badge>
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {row.challengeLabChallenges > 0 ? (
                            <>{row.challengeLabChallenges} {row.challengeLabChallenges === 1 ? 'reto' : 'retos'} · {row.challengeLabSolutions} {row.challengeLabSolutions === 1 ? 'solución' : 'soluciones'}</>
                          ) : (
                            <span className="text-muted-foreground">Sin retos</span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row.diagnosticCompleted ? (
                          <Badge variant="success">Completado</Badge>
                        ) : (
                          <Badge variant="secondary">Pendiente</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => setProjectsModal(row)}
                          className="text-sm hover:underline cursor-pointer"
                          title="Ver detalle de proyectos"
                        >
                          {row.projectsSubmitted}/{row.projectsCount} enviados
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Content Modal */}
      <Dialog open={!!contentModal} onOpenChange={(open) => !open && setContentModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Contenido de {contentModal?.full_name}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              {scopeMode === 'total' ? 'Mostrando todo la ruta' : `Mostrando hasta semana ${currentWeek}`}
            </p>
          </DialogHeader>
          {contentModal && (
            <div className="space-y-3">
              <div className="text-sm">
                Progreso: <span className="font-bold">{contentModal.contentItems.filter(c => c.completed).length}/{contentModal.contentItems.length}</span> ({contentModal.contentPercent}%)
              </div>
              {contentModal.contentItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hay contenido obligatorio configurado para esta ruta</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contenido obligatorio</TableHead>
                      <TableHead className="text-right">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contentModal.contentItems.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="text-sm">{item.title}</TableCell>
                        <TableCell className="text-right">
                          {item.completed ? (
                            <Badge variant="success" className="text-xs gap-1"><Check className="w-3 h-3" /> Visto</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs gap-1"><X className="w-3 h-3" /> Pendiente</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliverables Modal */}
      <Dialog open={!!deliverableModal} onOpenChange={(open) => !open && setDeliverableModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-primary" />
              Entregables de {deliverableModal?.full_name}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              {scopeMode === 'total' ? 'Mostrando todo la ruta' : `Mostrando hasta semana ${currentWeek}`}
            </p>
          </DialogHeader>
          {deliverableModal && (
            <div className="space-y-3">
              <div className="flex gap-3 text-sm">
                <span>Enviados: <span className="font-bold">{deliverableModal.deliverablesSubmitted}/{deliverableModal.deliverablesTotal || '?'}</span></span>
                <span className="text-success">Validados: <span className="font-bold">{deliverableModal.deliverablesValidated}</span></span>
              </div>
              {deliverableModal.deliverableItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hay entregables configurados</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sesión en Vivo</TableHead>
                      <TableHead>Entregable</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliverableModal.deliverableItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate" title={item.workshop_title}>
                          {item.workshop_title}
                        </TableCell>
                        <TableCell className="font-medium text-sm">{item.name}</TableCell>
                        <TableCell>{renderDeliverableStatusBadge(item.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1 justify-end">
                            {item.file_url ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Descargar archivo"
                                onClick={() => window.open(item.file_url!, '_blank')}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground w-7 inline-block text-center">—</span>
                            )}
                            {isSuperAdmin && item.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Eliminar entregable"
                                onClick={() => setDeliverableToDelete(item)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Projects Modal */}
      <Dialog open={!!projectsModal} onOpenChange={(open) => !open && setProjectsModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-accent" />
              Proyectos de {projectsModal?.full_name}
            </DialogTitle>
          </DialogHeader>
          {projectsModal && (
            <div className="space-y-3">
              <div className="text-sm">
                Enviados: <span className="font-bold">{projectsModal.projectsSubmitted}/{projectsModal.projectsCount}</span>
              </div>
              {projectsModal.projectItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Este participante no ha creado proyectos</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proyecto</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectsModal.projectItems.map(p => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-sm">{p.name}</TableCell>
                        <TableCell>
                          {p.submitted ? (
                            <Badge variant="success" className="text-xs">Enviado</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Borrador</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1"
                            onClick={() => navigate(`/ai-portfolio/${p.id}`)}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de eliminación de entregable */}
      <AlertDialog open={!!deliverableToDelete} onOpenChange={(open) => !open && setDeliverableToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Eliminar entregable
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar el entregable <span className="font-medium text-foreground">"{deliverableToDelete?.name}"</span>
              {deliverableToDelete?.workshop_title && deliverableToDelete.workshop_title !== 'Sin sesión en vivo' && (
                <> de la sesión <span className="font-medium text-foreground">"{deliverableToDelete.workshop_title}"</span></>
              )}
              ? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDeliverable.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteDeliverable.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deliverableToDelete?.id) deleteDeliverable.mutate(deliverableToDelete.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDeliverable.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando…</>
              ) : (
                <>Eliminar</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
